type CleanupTask = {
  executeAt: number
  callback: () => void
}

/**
 * Batches many GC registrations behind a single shared timeout.
 */
export class CleanupQueue {
  private static instance: CleanupQueue | null = null

  private tasks: Map<unknown, CleanupTask> = new Map()

  private timeoutId: ReturnType<typeof setTimeout> | null = null
  private microtaskScheduled = false
  // Some runtimes lack `performance` although the DOM types declare it.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  private readonly now = () => globalThis.performance?.now() ?? Date.now()

  private constructor() {}

  public static getInstance(): CleanupQueue {
    if (!CleanupQueue.instance) {
      CleanupQueue.instance = new CleanupQueue()
    }
    return CleanupQueue.instance
  }

  /**
   * Queues a cleanup task and defers timeout selection to a microtask so
   * multiple synchronous registrations can share one root timer.
   */
  public schedule(key: unknown, gcTime: number, callback: () => void): void {
    const executeAt = this.now() + gcTime
    this.tasks.set(key, { executeAt, callback })

    if (!this.microtaskScheduled) {
      this.microtaskScheduled = true
      Promise.resolve().then(() => {
        this.microtaskScheduled = false
        this.updateTimeout()
      })
    }
  }

  public cancel(key: unknown): void {
    this.tasks.delete(key)

    // Retire the root timer with the last task. A non-empty queue keeps its
    // timer even when the cancelled task was the earliest: it wakes early,
    // finds nothing due and reschedules, which costs less than rescanning
    // every task on each cancellation.
    if (this.tasks.size === 0 && this.timeoutId !== null) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
  }

  /**
   * Keeps only one active timeout: whichever task is due next.
   */
  private updateTimeout(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }

    if (this.tasks.size === 0) {
      return
    }

    let earliestTime = Infinity
    for (const task of this.tasks.values()) {
      if (task.executeAt < earliestTime) {
        earliestTime = task.executeAt
      }
    }

    const delay = Math.max(0, earliestTime - this.now())
    this.timeoutId = setTimeout(() => this.process(), delay)
    // Background collection GC must not keep an otherwise finished Node
    // process alive. Browsers return a numeric timer handle.
    if (typeof this.timeoutId === `object`) this.timeoutId.unref()
  }

  /**
   * Runs every task whose deadline has passed, then schedules the next wakeup
   * if there is still pending work.
   */
  private process(): void {
    this.timeoutId = null
    const now = this.now()
    for (const [key, task] of this.tasks.entries()) {
      if (now >= task.executeAt) {
        this.tasks.delete(key)
        try {
          task.callback()
        } catch (error) {
          console.error('Error in CleanupQueue task:', error)
        }
      }
    }

    if (this.tasks.size > 0) {
      this.updateTimeout()
    }
  }
}
